import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Users, Sparkles, Timer, HeartHandshake, Search, ShieldCheck,
  KeyRound, Copy, CheckCircle2, X, AlertTriangle, ArrowUpDown,
} from "lucide-react";
import { toast, Toaster } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso.slice(0, 10); }
}
function fmtMinutes(m) {
  if (!m) return "0h";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

const STAT_TILES = [
  { key: "total_users",  label: "People here",           Icon: Users,          accent: "var(--primary)" },
  { key: "active_last_7_days", label: "Active this week", Icon: Sparkles,      accent: "var(--accent)" },
  { key: "total_focus_sessions_completed", label: "Focus sessions", Icon: Timer, accent: "var(--primary)" },
  { key: "total_checkins", label: "Check-ins",            Icon: HeartHandshake, accent: "var(--accent)" },
];

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = !!(user && user.is_admin);

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users?limit=500"),
        ]);
        setStats(s.data);
        setItems(u.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items;
    if (q) {
      list = list.filter((r) =>
        (r.email || "").toLowerCase().includes(q) ||
        (r.dream_north_star_short || "").toLowerCase().includes(q)
      );
    }
    const { key, dir } = sort;
    const s = [...list].sort((a, b) => {
      const va = a[key] ?? "";
      const vb = b[key] ?? "";
      if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
      return dir === "asc"
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    return s;
  }, [items, query, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const onResetSuccess = (targetId, note) => {
    // If admin resets their own password they'll be logged out on next request;
    // just show the toast — the AuthContext will bounce them.
    setResetTarget(null);
    toast.success(note || "Password reset.");
    // refresh the user row to reflect any new metadata (nothing much to update
    // right now, but keep for future).
  };

  if (!isAdmin) {
    return (
      <AppShell>
        <Toaster richColors position="top-center" />
        <div className="max-w-xl mx-auto card-soft p-8 text-center fade-up" data-testid="admin-forbidden">
          <div className="font-display text-3xl mb-2" style={{ color: "var(--text)" }}>
            You don't have access to this page
          </div>
          <p className="text-sm text-muted mb-6">This corner of Ascend is for account management. If you think this is a mistake, ask an admin.</p>
          <a href="/dashboard" className="btn-primary inline-block" data-testid="admin-forbidden-back">Back to your dashboard</a>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="fade-up space-y-8" data-testid="admin-dashboard">
        <header>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck size={20} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
            <div className="text-xs uppercase tracking-widest text-muted">Admin</div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl" style={{ color: "var(--text)" }}>Caretaker's dashboard</h1>
          <p className="font-hand text-lg mt-2" style={{ color: "var(--accent)" }}>metadata only, quietly kept</p>
        </header>

        {/* Stats tiles */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-stats-grid">
          {STAT_TILES.map(({ key, label, Icon, accent }) => (
            <div key={key} className="card-soft p-5" data-testid={`admin-stat-${key}`}>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted">
                <Icon size={14} strokeWidth={1.5} style={{ color: accent }} />
                {label}
              </div>
              <div className="font-display text-3xl mt-2" style={{ color: "var(--text)" }}>
                {stats ? (stats[key] ?? 0) : "…"}
              </div>
            </div>
          ))}
        </section>

        {/* Users */}
        <section className="card-soft p-5 sm:p-7">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-4">
            <div>
              <div className="font-display text-2xl" style={{ color: "var(--text)" }}>Everyone here</div>
              <div className="text-xs text-muted">Metadata only. Journal, gratitude, and dream text stay private.</div>
            </div>
            <div className="sm:ml-auto relative">
              <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
              <input
                className="input-soft pl-9"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search email or north star…"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-muted text-sm" data-testid="admin-loading">a moment…</div>
          ) : filtered.length === 0 ? (
            <div className="text-muted text-sm" data-testid="admin-empty">No matches.</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                    <Th onClick={() => toggleSort("email")} label="Email" sort={sort} col="email" />
                    <Th label="North star" />
                    <Th onClick={() => toggleSort("aesthetic_preset")} label="Vibe" sort={sort} col="aesthetic_preset" />
                    <Th onClick={() => toggleSort("created_at")} label="Joined" sort={sort} col="created_at" />
                    <Th onClick={() => toggleSort("last_login_at")} label="Last login" sort={sort} col="last_login_at" />
                    <Th onClick={() => toggleSort("streak_days")} label="Streak" sort={sort} col="streak_days" />
                    <Th onClick={() => toggleSort("total_study_minutes")} label="Focus" sort={sort} col="total_study_minutes" />
                    <Th onClick={() => toggleSort("checkin_count")} label="Check-ins" sort={sort} col="checkin_count" />
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} className="border-t" style={{ borderColor: "var(--divider)" }} data-testid={`admin-user-row-${row.id}`}>
                      <td className="py-3 px-3 align-top">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium" style={{ color: "var(--text)" }}>{row.email}</span>
                          {row.is_admin && (
                            <span
                              data-testid="admin-badge"
                              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                              style={{ background: "color-mix(in oklab, var(--primary) 15%, transparent)", color: "var(--primary)" }}
                            >
                              Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 align-top max-w-[240px]">
                        <div className="truncate font-hand" style={{ color: "var(--accent)" }} title={row.dream_north_star_short || ""}>
                          {(row.dream_north_star_short || "").slice(0, 40) || "—"}
                        </div>
                      </td>
                      <td className="py-3 px-3 align-top text-muted text-xs">{row.aesthetic_preset}</td>
                      <td className="py-3 px-3 align-top text-muted">{fmtDate(row.created_at)}</td>
                      <td className="py-3 px-3 align-top text-muted">{fmtDate(row.last_login_at)}</td>
                      <td className="py-3 px-3 align-top">{row.streak_days || 0}d</td>
                      <td className="py-3 px-3 align-top">{fmtMinutes(row.total_study_minutes)}</td>
                      <td className="py-3 px-3 align-top">{row.checkin_count || 0}</td>
                      <td className="py-3 px-3 align-top text-right">
                        <button
                          className="btn-ghost inline-flex items-center gap-1.5 text-xs"
                          onClick={() => setResetTarget(row)}
                          data-testid={`admin-reset-btn-${row.id}`}
                          aria-label={`Reset password for ${row.email}`}
                        >
                          <KeyRound size={13} strokeWidth={1.5} />
                          Reset
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onSuccess={(note) => onResetSuccess(resetTarget.id, note)}
        />
      )}
    </AppShell>
  );
}

function Th({ label, onClick, sort, col }) {
  const active = sort && sort.key === col;
  return (
    <th
      className={`py-2 px-3 whitespace-nowrap ${onClick ? "cursor-pointer select-none" : ""}`}
      onClick={onClick}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {onClick && (
          <ArrowUpDown size={11} strokeWidth={1.5} style={{ opacity: active ? 1 : 0.4 }} />
        )}
      </span>
    </th>
  );
}

function ResetPasswordModal({ target, onClose, onSuccess }) {
  const [mode, setMode] = useState("generate"); // "generate" | "specific"
  const [specific, setSpecific] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {temp_password?, message}
  const [copied, setCopied] = useState(false);

  const emailMatches = confirmEmail.trim().toLowerCase() === (target.email || "").toLowerCase();
  const canSubmit = emailMatches && !busy && (mode === "generate" || specific.length >= 8);

  const submit = async () => {
    setBusy(true);
    try {
      let data;
      if (mode === "generate") {
        ({ data } = await api.post(`/admin/users/${target.id}/generate-temp-password`));
      } else {
        ({ data } = await api.post(`/admin/users/${target.id}/reset-password`, { new_password: specific }));
      }
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4 py-8"
      style={{ background: "color-mix(in oklab, var(--text) 40%, transparent)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      data-testid="admin-reset-modal"
    >
      <div className="w-full max-w-lg card-soft p-6 sm:p-8 relative">
        <button
          className="absolute top-3 right-3 btn-ghost p-2"
          onClick={onClose}
          disabled={busy}
          data-testid="admin-reset-close"
          aria-label="Close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        {!result ? (
          <>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={16} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              <div className="text-xs uppercase tracking-wider text-muted">Reset password</div>
            </div>
            <h2 className="font-display text-2xl" style={{ color: "var(--text)" }} data-testid="admin-reset-target">
              {target.email}
            </h2>
            <div className="mt-4 mb-5 text-sm px-3 py-3 rounded-xl flex items-start gap-2"
                 style={{ background: "color-mix(in oklab, var(--accent) 12%, transparent)", color: "var(--text)" }}>
              <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5" style={{ color: "var(--accent)" }} />
              <div>
                This will log <b>{target.email}</b> out of every device and require them to sign in again with the new password.
              </div>
            </div>

            <div className="flex gap-2 mb-4" role="tablist">
              <button
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mode==="generate"?"bg-[var(--surface-2)]":""}`}
                style={{ color: mode==="generate" ? "var(--primary)" : "var(--text-muted)" }}
                onClick={() => setMode("generate")}
                data-testid="admin-reset-mode-generate"
              >Generate temporary password</button>
              <button
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${mode==="specific"?"bg-[var(--surface-2)]":""}`}
                style={{ color: mode==="specific" ? "var(--primary)" : "var(--text-muted)" }}
                onClick={() => setMode("specific")}
                data-testid="admin-reset-mode-specific"
              >Set a specific password</button>
            </div>

            {mode === "specific" && (
              <div className="mb-4">
                <label className="text-xs uppercase tracking-wider text-muted mb-1 block">New password (min 8 chars)</label>
                <input
                  type="text"
                  className="input-soft font-mono"
                  value={specific}
                  onChange={(e) => setSpecific(e.target.value)}
                  minLength={8}
                  autoFocus
                  data-testid="admin-reset-specific-input"
                  placeholder="at least 8 characters"
                />
              </div>
            )}

            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider text-muted mb-1 block">
                Type <b>{target.email}</b> to confirm
              </label>
              <input
                type="text"
                className="input-soft"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                autoComplete="off"
                data-testid="admin-reset-confirm-input"
                placeholder={target.email}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button className="btn-ghost" onClick={onClose} disabled={busy} data-testid="admin-reset-cancel">Cancel</button>
              <button
                className="btn-primary disabled:opacity-60"
                onClick={submit}
                disabled={!canSubmit}
                data-testid="admin-reset-submit"
              >
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 size={16} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
              <div className="text-xs uppercase tracking-wider text-muted">Done</div>
            </div>
            <h2 className="font-display text-2xl" style={{ color: "var(--text)" }}>
              {result.target_email} has been logged out
            </h2>
            <p className="text-sm text-muted mt-2">{result.message}</p>

            {result.temp_password && (
              <div className="mt-5" data-testid="admin-reset-temp-block">
                <div className="text-xs uppercase tracking-wider text-muted mb-2">Temporary password (shown once)</div>
                <div className="flex items-stretch gap-2">
                  <div
                    className="flex-1 font-mono text-lg px-4 py-3 rounded-xl select-all"
                    style={{ background: "var(--surface-2)", color: "var(--text)" }}
                    data-testid="admin-reset-temp-value"
                  >
                    {result.temp_password}
                  </div>
                  <button
                    className="btn-primary inline-flex items-center gap-1.5"
                    onClick={copyPassword}
                    data-testid="admin-reset-copy-btn"
                  >
                    {copied ? <CheckCircle2 size={14} strokeWidth={1.75} /> : <Copy size={14} strokeWidth={1.75} />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <div className="text-xs mt-3" style={{ color: "var(--accent)" }}>
                  This is the only time you'll see this password. Copy it now and share it with {result.target_email} through a safe channel.
                </div>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                className="btn-primary"
                onClick={() => { onSuccess(result.message); }}
                data-testid="admin-reset-done"
              >Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
